import React from "react";
import { useSearchParams } from "react-router-dom";
import MessengerLayout   from "../../../layout/MessengerLayout";
import PoliciesTab       from "./tabs/PoliciesTab";
import LettersTab        from "./tabs/LettersTab";
import WorkflowsTab      from "./tabs/WorkflowsTab";
import ComplianceTab     from "./tabs/ComplianceTab";
import ChecklistsTab     from "./tabs/ChecklistsTab";
import AuditLogTab       from "./tabs/AuditLogTab";
import ContactsTab       from "./tabs/ContactsTab";
import OrganisationTab   from "./tabs/OrganisationTab";
// THE WORKFLOW LIBRARY IS MOUNTED HERE, NOT REBUILT HERE. Same component
// as the /admin/workflow-engine route; `embedded` varies page chrome
// only. Importing the body directly rather than wrapping it in a
// one-line tab file is deliberate — it makes "this is not a second
// implementation" visible at the import.
import { WorkflowEngineLibraryBody } from "../../../pages/admin/WorkflowEngineLibrary";

// ======================================================================
// TWO DIFFERENT FEATURES WERE BOTH CALLED "WORKFLOWS"
// ======================================================================
//
// `workflows` here is now the workflow-engine catalogue — 57 definitions
// covering every HR process the platform runs. `approval_chains` is the
// legacy feature that used to hold the name: role + label +
// escalate_after_hours + required, on the `workflows` table at
// /api/admin/workflows. D2 called for the rename and this is it.
//
// THE COLLISION WAS WORSE THAN A NAMING PROBLEM. Anyone looking for the
// catalogue came to Admin Settings → Workflows, landed on the legacy tab
// with nothing configured in it, and read "No workflows configured" as
// "the feature is broken" rather than "wrong tab". So the engine takes
// the name AND the slot the old tab sat in, and the legacy one sits
// immediately beside it where somebody who actually wanted approval
// routing will find it.
//
// The legacy table, its routes and WorkflowsTab.jsx's behaviour are
// untouched. Only labels changed.
const TABS = [
  { key: "organisation",    label: "Organisation"    },
  { key: "policies",        label: "Policies"        },
  { key: "letters",         label: "Letters"         },
  { key: "workflows",       label: "Workflows"       },
  { key: "approval_chains", label: "Approval chains" },
  { key: "compliance",      label: "Compliance"      },
  { key: "checklists",      label: "Checklists"      },
  { key: "contacts",        label: "Contacts"        },
  { key: "auditlog",        label: "Audit Log"       },
];

const TAB_KEYS = TABS.map((t) => t.key);

export default function AdminDocumentsPage() {
  // THE TAB IS IN THE URL so it can be linked to. The builder's "back"
  // has to return somebody to the tab they came from, and without this
  // it would drop them on Organisation — which is the same "where did my
  // screen go" problem this change exists to fix, one level down.
  // Unknown or absent ?tab= falls back to Organisation rather than
  // rendering nothing.
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("tab");
  const tab = TAB_KEYS.includes(fromUrl) ? fromUrl : "organisation";
  const setTab = (key) => setParams({ tab: key }, { replace: true });

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Admin Settings</h1>
          <p className="text-white/40 text-sm mt-0.5">
            Organisation setup, documents, policies, workflows, approval
            chains and compliance
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/5 pb-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 rounded-t-lg border-b-2 transition-colors whitespace-nowrap ${
                tab === t.key
                  ? "border-[#00ff90] text-[#00ff90]"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "organisation"    && <OrganisationTab />}
        {tab === "policies"        && <PoliciesTab />}
        {tab === "letters"         && <LettersTab />}
        {tab === "workflows"       && <WorkflowEngineLibraryBody embedded />}
        {tab === "approval_chains" && <WorkflowsTab />}
        {tab === "compliance"      && <ComplianceTab />}
        {tab === "checklists"      && <ChecklistsTab />}
        {tab === "contacts"        && <ContactsTab />}
        {tab === "auditlog"        && <AuditLogTab />}
      </div>
    </MessengerLayout>
  );
}